import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { ApiError } from './api/client';
import { Layout } from './Layout';
import { AcceptInvitePage, LoginPage, ResetPasswordPage } from './pages/Public';
import { SessionProvider } from './session';
import './styles.css';
import { ErrorState, Loading, ToastProvider } from './ui';

// Páginas carregadas sob demanda (o player HLS só é baixado quando necessário).
const AuditPage = lazy(() => import('./pages/Audit').then((m) => ({ default: m.AuditPage })));
const CameraDetailPage = lazy(() => import('./pages/CameraDetail').then((m) => ({ default: m.CameraDetailPage })));
const CameraFormPage = lazy(() => import('./pages/CameraForm').then((m) => ({ default: m.CameraFormPage })));
const CamerasPage = lazy(() => import('./pages/Cameras').then((m) => ({ default: m.CamerasPage })));
const DashboardPage = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.DashboardPage })));
const EventsPage = lazy(() => import('./pages/Events').then((m) => ({ default: m.EventsPage })));
const GatewaysPage = lazy(() => import('./pages/Gateways').then((m) => ({ default: m.GatewaysPage })));
const LivePage = lazy(() => import('./pages/Live').then((m) => ({ default: m.LivePage })));
const PlatformPage = lazy(() => import('./pages/Platform').then((m) => ({ default: m.PlatformPage })));
const RecordingsPage = lazy(() => import('./pages/Recordings').then((m) => ({ default: m.RecordingsPage })));
const SettingsPage = lazy(() => import('./pages/Settings').then((m) => ({ default: m.SettingsPage })));
const SitesPage = lazy(() => import('./pages/Sites').then((m) => ({ default: m.SitesPage })));
const StoragePage = lazy(() => import('./pages/Storage').then((m) => ({ default: m.StoragePage })));
const UsersPage = lazy(() => import('./pages/Users').then((m) => ({ default: m.UsersPage })));

try {
  document.documentElement.dataset.theme = localStorage.getItem('cftv.theme') ?? 'dark';
} catch {}

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <Routes>
      <Route path="/convite" element={<AcceptInvitePage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route
        path="*"
        element={
          <SessionProvider
            fallback={(state, retry, error) =>
              state === 'loading' ? <Loading label="Abrindo o painel…" /> : state === 'error' ? <ErrorState error={error} onRetry={retry} /> : <LoginPage onLoggedIn={retry} />
            }
          >
            {() => (
              <Routes>
                <Route element={<Layout />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="ao-vivo" element={<LivePage />} />
                  <Route path="gravacoes" element={<RecordingsPage />} />
                  <Route path="eventos" element={<EventsPage />} />
                  <Route path="cameras" element={<CamerasPage />} />
                  <Route path="cameras/nova" element={<CameraFormPage />} />
                  <Route path="cameras/:cameraId" element={<CameraDetailPage />} />
                  <Route path="locais" element={<SitesPage />} />
                  <Route path="gateways" element={<GatewaysPage />} />
                  <Route path="usuarios" element={<UsersPage />} />
                  <Route path="armazenamento" element={<StoragePage />} />
                  <Route path="auditoria" element={<AuditPage />} />
                  <Route path="configuracoes" element={<SettingsPage />} />
                  <Route path="plataforma" element={<PlatformPage />} />
                  <Route path="login" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<ErrorState error={new ApiError(404, 'not_found', 'Página não encontrada')} />} />
                </Route>
              </Routes>
            )}
          </SessionProvider>
        }
      />
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
